<script setup>
import { computed, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { toast } from 'dashboard/helper/toast';
import { useMapGetter } from 'dashboard/composables/store';
import { useKanbanPermissions } from 'dashboard/composables/useKanbanPermissions';

const props = defineProps({
  conversationId: {
    type: Number,
    required: true,
  },
  contactId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['created', 'close']);

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const { ownDealsOnly } = useKanbanPermissions();

const pipelines = computed(() => store.getters['kanban/pipelines']);
const uiFlags = computed(() => store.getters['kanban/uiFlags']);
const isSubmitting = computed(() => uiFlags.value.isCreatingDeal);

// Título nasce com o nome do contato, como o compositor em linha do quadro —
// era "Deal — 42" fixo, em inglês.
const contactName = computed(
  () => store.getters['contacts/getContact'](props.contactId)?.name || ''
);

const state = reactive({
  pipelineId: null,
  title:
    contactName.value ||
    t('KANBAN.DEAL_PANEL.DEFAULT_TITLE', { id: props.conversationId }),
  value: '',
  source: '',
});

const rules = {
  pipelineId: {
    required: helpers.withMessage(
      () => t('KANBAN.DEAL_PANEL.REQUIRED'),
      required
    ),
  },
  title: {
    required: helpers.withMessage(
      () => t('KANBAN.DEAL_PANEL.REQUIRED'),
      required
    ),
  },
};

const v$ = useVuelidate(rules, state);

onMounted(() => {
  if (!pipelines.value.length) {
    store.dispatch('kanban/fetchPipelines');
  }
});

const handleSubmit = async () => {
  const valid = await v$.value.$validate();
  if (!valid) return;

  const payload = {
    pipeline_id: state.pipelineId,
    contact_id: props.contactId,
    conversation_id: props.conversationId,
    title: state.title,
  };

  // Papel "só os cards que atende": se a conversa não é dele, o card sem dono
  // sairia do recorte no ato (o backend recusa). Quem cria vira o responsável.
  if (ownDealsOnly.value) {
    payload.owner_id = currentUser.value.id;
  }

  if (state.value !== '' && state.value !== null) {
    const numeric = parseFloat(state.value);
    if (!Number.isNaN(numeric)) {
      payload.value_cents = Math.round(numeric * 100);
    }
  }

  if (state.source) {
    payload.source = state.source;
  }

  try {
    await store.dispatch('kanban/createDeal', payload);
    toast.success(t('KANBAN.DEAL_PANEL.CREATED_SUCCESS'));
    emit('created');
    emit('close');
  } catch (error) {
    toast.error(
      error?.response?.data?.error || t('KANBAN.DEAL_PANEL.CREATE_ERROR')
    );
  }
};

const handleCancel = () => emit('close');
</script>

<template>
  <form
    class="flex flex-col gap-3 p-3 mt-2 rounded-md border border-n-weak bg-n-alpha-1"
    @submit.prevent="handleSubmit"
  >
    <h4 class="text-sm font-semibold text-n-slate-12">
      {{ t('KANBAN.DEAL_PANEL.CREATE_TITLE') }}
    </h4>

    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium text-n-slate-11">
        {{ t('KANBAN.DEAL_PANEL.FIELD_PIPELINE') }}
      </label>
      <select
        v-model="state.pipelineId"
        class="text-sm rounded-md border border-n-weak bg-n-background px-2 py-1.5 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand-ink"
        :class="{ 'border-n-ruby-8': v$.pipelineId.$error }"
        @blur="v$.pipelineId.$touch()"
      >
        <option :value="null" disabled>
          {{ t('KANBAN.DEAL_PANEL.FIELD_PIPELINE') }}
        </option>
        <option
          v-for="pipeline in pipelines"
          :key="pipeline.id"
          :value="pipeline.id"
        >
          {{ pipeline.name }}
        </option>
      </select>
      <span v-if="v$.pipelineId.$error" class="text-[11px] text-n-ruby-9">
        {{ v$.pipelineId.$errors[0]?.$message }}
      </span>
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium text-n-slate-11">
        {{ t('KANBAN.DEAL_PANEL.FIELD_TITLE') }}
      </label>
      <input
        v-model="state.title"
        type="text"
        class="text-sm rounded-md border border-n-weak bg-n-background px-2 py-1.5 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand-ink"
        :class="{ 'border-n-ruby-8': v$.title.$error }"
        @blur="v$.title.$touch()"
      />
      <span v-if="v$.title.$error" class="text-[11px] text-n-ruby-9">
        {{ v$.title.$errors[0]?.$message }}
      </span>
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium text-n-slate-11">
        {{ t('KANBAN.DEAL_PANEL.FIELD_VALUE') }}
      </label>
      <input
        v-model="state.value"
        type="number"
        min="0"
        step="0.01"
        class="text-sm rounded-md border border-n-weak bg-n-background px-2 py-1.5 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand-ink"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-xs font-medium text-n-slate-11">
        {{ t('KANBAN.DEAL_PANEL.FIELD_SOURCE') }}
      </label>
      <input
        v-model="state.source"
        type="text"
        class="text-sm rounded-md border border-n-weak bg-n-background px-2 py-1.5 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand-ink"
      />
    </div>

    <div class="flex items-center justify-end gap-2 mt-1">
      <button
        type="button"
        class="text-xs font-medium px-3 py-1.5 rounded-md text-n-slate-11 hover:bg-n-alpha-2"
        :disabled="isSubmitting"
        @click="handleCancel"
      >
        {{ t('KANBAN.DEAL_PANEL.CANCEL') }}
      </button>
      <button
        type="submit"
        class="text-xs font-semibold px-3 py-1.5 rounded-md bg-n-brand text-white hover:opacity-90 disabled:opacity-50"
        :disabled="isSubmitting"
      >
        {{ t('KANBAN.DEAL_PANEL.SUBMIT') }}
      </button>
    </div>
  </form>
</template>
